import React, { useState, useEffect } from "react";
import { Form, Row, InputGroup } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import {
  CButton,
  CCol,
  CFormInput,
  CFormLabel,
  CFormFeedback,
} from "@coreui/react";
import { getMasterData } from "../../../redux/slices/CustomerSlice";
import { useDispatch } from "react-redux";
import FormWrapper from "../../../pagecomponents/Common/FormWrapper";
import { searchData } from "../../../redux/slices/MasterLedgerVoucherSlice.js";
import { addData, editVchData } from "../../../redux/slices/VoucherSlice";
import { checkEmpty, toastMessage } from "../../../helpers/utility.js";

const INIT_APPLICABLE_RATE_FORM_DATA = {
  voucher_payment_Id: 0,
  voucher_payment_Data: "",
  voucher_payment_Name: "",
  voucher_payment_Type: "Ledger",
  voucher_payment_Amount: 0,
  type: "",
};

const INIT_FORM_DATA = {
  voucher_Type: "Expense",
  voucher_Date: format(new Date(), "yyyy-MM-dd"),
  voucher_No: "",
  voucher_Payment_Mode: "",
  voucher_Note: "",
  invoice_Id: "",
  client_Id: 1,
  voucher_Id: 0,
  staff_Id: 1,
  voucher_payment: [],
};

const ExpensesVchAddEdit = ({ invisible, loadData, mode, editData }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [masterData, setMasterData] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [applicableRateFormData, setApplicableRateFormData] = useState(
    INIT_APPLICABLE_RATE_FORM_DATA
  );
  const [applicableRate, setApplicableRate] = useState([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  useEffect(() => {
    fetchMasterData();
    document
      .querySelector("input[name='voucher_payment_Name']")
      .setAttribute("autocomplete", "off");
  }, [mode, editData, dispatch]); // Dependency array to prevent unnecessary re-renders

  const fetchMasterData = async () => {
    let PARAMS = {};
    const { payload } = await dispatch(getMasterData({ inputData: PARAMS }));
    if (payload?.status === "error") {
      console.error("Error fetching data:", payload.msg);
    } else {
      //   console.log(payload.payment_mode);
      setMasterData(payload);
    }
  };

  useEffect(() => {
    if (mode && editData) {
      //   console.log("Incoming data:", editData);

      setFormData((prev) => ({
        ...prev,
        voucher_Type: "Expense",
        voucher_Date: format(new Date(editData.voucher_Date), "yyyy-MM-dd"),
        voucher_No: editData.voucher_No,
        voucher_Payment_Mode: editData.voucher_Payment_Mode,
        reference_no: editData.reference_no,
        voucher_Note: editData.voucher_Note,
        invoice_Id: editData.invoice_Id,
        client_Id: 1,
        voucher_Id: editData.voucher_Id,
        staff_Id: 1,
        voucher_payment:
          editData.voucher_payment?.length > 0
            ? editData.voucher_payment.map((item) => ({
                voucher_payment_Id: item.voucher_payment_Id,
                voucher_payment_Data: item.voucher_payment_Data,
                voucher_payment_Name: item.voucher_payment_Name,
                voucher_payment_Type: item.voucher_payment_Type,
                voucher_payment_Amount: item.voucher_payment_Amount,
                type: "",
              }))
            : [],
      }));

      setApplicableRate(editData.voucher_payment || []); // Ensure applicableRate is also updated
    }
  }, [mode, editData]);

  // const handleInputChange = (e) => {
  //     const { name, value } = e.target;
  //     if (name === "voucher_Date") {
  //         setFormData({
  //             ...formData,
  //             [name]: format(new Date(value), "yyyy-MM-dd"), // Format the date
  //         });
  //     } else {
  //         setFormData({
  //             ...formData,
  //             [name]: value,
  //         });
  //     }
  // };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const updatedFormData = { ...formData }; // Start with old form data

    if (name === "voucher_Payment_Mode") {
      const selectedOption = masterData.payment_mode?.find(
        (item) => item.payment_mode_master_Id == value
      );

      if (selectedOption) {
        updatedFormData[name] = value;
        updatedFormData.payment_mode_Name = selectedOption.payment_mode_Name;
        updatedFormData.payment_mode_Code = selectedOption.payment_mode_Code;
      }
    } else if (name === "voucher_Date") {
      updatedFormData[name] = format(new Date(value), "yyyy-MM-dd");
    } else {
      updatedFormData[name] = value;
    }

    setFormData(updatedFormData); // Set state once
  };

  const handleSave = async () => {
    if (checkEmpty(formData.voucher_Payment_Mode)) {
      toastMessage("error", "Payment mode is required.");
      return;
    } else if (
      !formData.voucher_payment ||
      formData.voucher_payment.filter((item) => item.type !== "delete")
        .length === 0
    ) {
      toastMessage("error", "Please add at least one ledger with amount");
    } else {
      setIsSaveDisabled(true);
      let api = mode ? editVchData : addData;
      const { payload } = await dispatch(api({ inputData: formData }));
      if (payload?.status === "error") {
        setIsSaveDisabled(false);
        toastMessage("error", payload.msg || "An error occurred while saving.");
      } else {
        setIsSaveDisabled(false);
        toastMessage(
          payload?.status || "success",
          payload?.msg || "Operation successful."
        );
        invisible();
        loadData();
      }
    }
  };

  const handleAutoComplete = async (e) => {
    const { name, value } = e.target;
    setApplicableRateFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (value.trim() !== "") {
      try {
        let params = {
          skip: 0,
          take: 1000,
          searchStr: value,
          sorting: "asc",
          sorting_key: "created_at",
          filter: {},
        };
        const { payload } = await dispatch(searchData({ inputData: params }));
        setSuggestions(payload.data);
      } catch (error) {
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  };

  const setLedgerData = (supplierData) => {
    setApplicableRateFormData((prevState) => ({
      ...prevState,
      voucher_payment_Name: supplierData?.ledger_Name,
      voucher_payment_Data: supplierData?.ledger_Id,
    }));

    setSuggestions([]); // Clear suggestions
  };

  const setVoucherAmount = (e) => {
    let { value } = e.target;

    if (value.length > 1 && value.startsWith("0")) {
      value = value.replace(/^0+/, "");
    }
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      // Allow only numbers and up to 2 decimal places
      setApplicableRateFormData((prevState) => ({
        ...prevState,
        voucher_payment_Amount: value,
      }));
    }
  };

  const formatText = (text) => {
    return text
      .replace(/_/g, " ") // Replace underscores with spaces
      .split(" ") // Split into words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter
      .join(" "); // Join back into a string
  };

  const addApplicableRate = () => {
    if (checkEmpty(applicableRateFormData.voucher_payment_Name)) {
      toastMessage("error", "Search Your Expenses");
    } else if (checkEmpty(applicableRateFormData.voucher_payment_Amount)) {
      toastMessage("error", "Enter Amount");
    } else {
      setApplicableRate((prevArray) => {
        const updatedRates = [...prevArray, applicableRateFormData];

        // Update formData using the latest applicableRate
        setFormData((prevFormData) => ({
          ...prevFormData,
          voucher_payment: updatedRates,
        }));

        return updatedRates;
      });

      setApplicableRateFormData(INIT_APPLICABLE_RATE_FORM_DATA);
    }
  };

  const removeApplicableRate = (idToRemove) => {
    setApplicableRate((prevArray) => {
      const updatedRates = prevArray.map((item) =>
        item.voucher_payment_Id === idToRemove
          ? { ...item, type: "delete" }
          : item
      );

      // Update formData with the modified applicableRate
      setFormData((prevFormData) => ({
        ...prevFormData,
        voucher_payment: updatedRates,
      }));

      return updatedRates;
    });
  };

  return (
    <FormWrapper onSubmit={handleSave}>
      <Row className="row-xs mb-2">
        <CCol md={4}>
          <CFormLabel
            className="main-content-label tx-11 tx-medium tx-gray-600"
            htmlFor="voucher_Date"
          >
            Vch Date
          </CFormLabel>
          <InputGroup className="input-group reactdate-pic">
            <InputGroup.Text className="input-group-text">
              <i className="typcn typcn-calendar-outline tx-24 lh--9 op-6"></i>
            </InputGroup.Text>
            <DatePicker
              className="form-control"
              name="voucher_Date"
              id="voucher_Date"
              selected={
                formData.voucher_Date ? new Date(formData.voucher_Date) : null
              } // Convert to Date object
              onChange={(date) => {
                const event = {
                  target: {
                    name: "voucher_Date",
                    value: date,
                  },
                };
                handleInputChange(event); // Simulate an event for compatibility
              }}
              dateFormat="yyyy-MM-dd" // Match the desired format
              // isClearable // Optional: Allow clearing the date
            />
          </InputGroup>
          <CFormFeedback invalid>Enter Voucher Date</CFormFeedback>
        </CCol>
        {/* <CCol md={3}>
                    <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="voucher_No">Vch No</CFormLabel>
                    <CFormInput
                        type="text"
                        id="voucher_No"
                        name="voucher_No"
                        value={formData.voucher_No}
                        onChange={handleInputChange}
                        required
                    />
                    <CFormFeedback invalid>Enter Voucher No</CFormFeedback>
                </CCol> */}
        <CCol md={4}>
          <CFormLabel
            className="main-content-label tx-11 tx-medium tx-gray-600"
            htmlFor="voucher_Payment_Mode"
          >
            Paid By
          </CFormLabel>
          <Form.Select
            className="form-control"
            aria-label="Paid By"
            name="voucher_Payment_Mode"
            value={formData.voucher_Payment_Mode}
            onChange={handleInputChange}
          >
            <option value="">Choose...</option>
            {/* Map over the options array to generate the option elements dynamically */}
            {masterData.payment_mode?.map((option) => (
              <option
                key={option.payment_mode_master_Id}
                value={option.payment_mode_master_Id}
              >
                {option.payment_mode_Name}
              </option>
            ))}
          </Form.Select>
          <CFormFeedback invalid>Select Paid By</CFormFeedback>
        </CCol>
        <CCol md={4}>
          <CFormLabel
            className="main-content-label tx-11 tx-medium tx-gray-600"
            htmlFor="reference_no"
          >
            Reference No <span style={{ color: "red" }}>*</span>
          </CFormLabel>
          <CFormInput
            type="text"
            id="reference_no"
            name="reference_no"
            value={formData.reference_no}
            onChange={handleInputChange}
            required
            invalid={!formData.reference_no} // if using CoreUI validation
          />
          <CFormFeedback invalid>Reference No is required</CFormFeedback>
        </CCol>
      </Row>
      <Row className="row-xs mb-2">
        <CCol md={12}>
          <CFormLabel
            className="main-content-label tx-11 tx-medium tx-gray-600"
            htmlFor="voucher_Note"
          >
            Narration
          </CFormLabel>
          <CFormInput
            type="text"
            id="voucher_Note"
            name="voucher_Note"
            value={formData.voucher_Note}
            onChange={handleInputChange}
            // required
          />
          <CFormFeedback invalid>Enter Narration</CFormFeedback>
        </CCol>
      </Row>
      <Row className="row-xs mb-2">
        <CCol md={7}>
          <CFormLabel
            className="main-content-label tx-11 tx-medium tx-gray-600"
            htmlFor="voucher_payment_Name"
          >
            Search Ledger
          </CFormLabel>
          <div className="hsc-wrapper">
            <InputGroup className="input-group">
              <CFormInput
                type="text"
                id="voucher_payment_Name"
                name="voucher_payment_Name"
                value={applicableRateFormData.voucher_payment_Name}
                onChange={handleAutoComplete}
                autoComplete="false"
              />
            </InputGroup>
            {suggestions && suggestions.length > 0 && (
              <div
                className="header-search-container"
                onMouseDown={(e) => e.preventDefault()}
                onPointerDown={(e) => e.preventDefault()}
              >
                <div className="hs">
                  {/* <Container fluid> */}
                  {/* <Row className="d-flex justify-content-between mt-3 align-items-stretch"> */}
                  {suggestions?.map((suggestion, index) => (
                    // <div>
                    <div
                      key={suggestion?.ledger_Id || index}
                      className="main-message-list header-search-element"
                      onPointerDown={(e) => {
                        e.preventDefault();
                        setLedgerData(suggestion);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <strong>{suggestion?.ledger_Name} </strong>
                      <span>{suggestion?.ledger_type}</span>
                    </div>
                    // </div>
                  ))}
                  {/* </Row> */}
                  {/* </Container> */}
                </div>
              </div>
            )}
          </div>
          <CFormFeedback invalid>Select or Add Supplier</CFormFeedback>
        </CCol>

        <CCol md={4}>
          <CFormLabel
            className="main-content-label tx-11 tx-medium tx-gray-600"
            htmlFor="voucher_payment_Amount"
          >
            Amount
          </CFormLabel>
          <CFormInput
            type="text"
            id="voucher_payment_Amount"
            name="voucher_payment_Amount"
            value={applicableRateFormData.voucher_payment_Amount}
            onChange={setVoucherAmount}
            required
          />
          <CFormFeedback invalid>Enter Amount</CFormFeedback>
        </CCol>

        <CCol md={1}>
          <CButton
            color="primary"
            className="btn btn-sm"
            style={{ marginTop: "30px" }}
            onClick={() => {
              addApplicableRate();
            }}
          >
            +
          </CButton>
        </CCol>
      </Row>
      {applicableRate
        ?.filter((item) => item.type !== "delete")
        .map((item, index) => (
          <Row className="row-xs mb-2" key={index}>
            <CCol md={7}>
              <CFormInput
                type="text"
                value={formatText(item.voucher_payment_Name)}
                disabled={true}
              />
            </CCol>
            <CCol md={4}>
              <CFormInput
                type="text"
                value={item.voucher_payment_Amount}
                disabled={true}
              />
            </CCol>
            <CCol md={1}>
              <CButton
                color="danger"
                className="btn btn-sm mt-1"
                onClick={() => removeApplicableRate(item.voucher_payment_Id)}
              >
                -
              </CButton>
            </CCol>
          </Row>
        ))}
      <Row className="row-xs mb-2 btn-animation">
        <CCol xs={12} className="mt-10">
          <CButton
            color="primary"
            className={`btn btn-sm float-end ${
              isSaveDisabled ? "btn-loaders" : ""
            }`}
            type="submit"
            disabled={isSaveDisabled}
          >
            {isSaveDisabled ? (
              <span className="loading">Save Changes</span>
            ) : (
              "Save Changes"
            )}
          </CButton>
          <CButton
            color="secondary"
            className="btn btn-sm float-end me-2"
            onClick={invisible}
            disabled={isSaveDisabled}
          >
            Close
          </CButton>
        </CCol>
      </Row>
    </FormWrapper>
  );
};

export default ExpensesVchAddEdit;
